import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { admin } from '../api';
import AdminLayout from '../components/AdminLayout';
import BusinessStatusBadge from '../components/BusinessStatusBadge';

const formatNumber = (n) => new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }) : '-');

const StatCard = ({ label, value }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
    <p className="text-sm text-gray-500">{label}</p>
    <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
  </div>
);

export default function AdminDashboard() {
  const [overview, setOverview] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    admin.overview().then((r) => setOverview(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      admin.businesses(search).then((r) => { setBusinesses(r.data); setLoading(false); });
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  return (
    <AdminLayout>
      <h1 className="text-xl font-bold text-gray-900 mb-6">Overview</h1>

      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="Total Businesses" value={overview.total_businesses} />
          <StatCard label="Total Invoices" value={overview.total_invoices} />
          <StatCard label="Total Revenue" value={formatNumber(overview.total_revenue)} />
          <StatCard label="New Signups This Month" value={overview.new_signups_this_month} />
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-gray-900">Businesses</h2>
          <input
            type="text"
            placeholder="Search by name or email..."
            className="input w-64"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-2 pr-4 text-gray-500 font-medium">Business</th>
                <th className="text-left py-2 pr-4 text-gray-500 font-medium">Owner Email</th>
                <th className="text-left py-2 pr-4 text-gray-500 font-medium">Country</th>
                <th className="text-left py-2 pr-4 text-gray-500 font-medium">Status</th>
                <th className="text-right py-2 pr-4 text-gray-500 font-medium">Invoices</th>
                <th className="text-right py-2 pr-6 text-gray-500 font-medium">Revenue</th>
                <th className="text-left py-2 pr-4 text-gray-500 font-medium">Last Active</th>
                <th className="text-left py-2 text-gray-500 font-medium">Joined</th>
              </tr>
            </thead>
            <tbody>
              {!loading && businesses.length === 0 && (
                <tr><td colSpan={8} className="py-8 text-center text-gray-400">No businesses found</td></tr>
              )}
              {businesses.map((b) => (
                <tr key={b.id} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="py-3 pr-4">
                    <Link to={`/admin/business/${b.id}`} className="text-primary-600 hover:underline font-medium">
                      {b.name}
                    </Link>
                  </td>
                  <td className="py-3 pr-4 text-gray-600">{b.owner_email || '—'}</td>
                  <td className="py-3 pr-4 text-gray-600">{b.country || '-'}</td>
                  <td className="py-3 pr-4"><BusinessStatusBadge status={b.status} /></td>
                  <td className="py-3 pr-4 text-right text-gray-700">{b.total_invoices}</td>
                  <td className="py-3 pr-6 text-right text-gray-900 font-medium">{formatNumber(b.total_revenue)}</td>
                  <td className="py-3 pr-4 text-gray-500">{formatDate(b.last_active)}</td>
                  <td className="py-3 text-gray-500">{formatDate(b.date_joined)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
